import { useNavigate } from 'react-router-dom';

import { Box, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';

import { hasReadPermission } from 'utils/permissions';

import useAuth from 'hooks/useAuth';

import Avatar from 'components/@extended/Avatar';

import { useGetPermissions } from 'api/authUser';
import { useGetCompanyLogoThumbnailUrl, useGetCompanyProfile } from 'api/Contractor/companyProfile';

interface Props {
    open: boolean;
}

function DrawerContractorCompanyHeader({ open }: Props) {
    const { user } = useAuth();
    const theme = useTheme();
    const isIcon = !open;
    const navigate = useNavigate()

    if (user?.userType !== 4) return null;


    const { permissions } = useGetPermissions();
    const featureId = 33;

    const allowView = hasReadPermission(featureId, permissions)

    if (!allowView) return null

    const { data: companyProfile, isLoading: companyLoading } = useGetCompanyProfile();

    const { data: logoUrl, isLoading } = useGetCompanyLogoThumbnailUrl()

    const contractorCompanyName = companyProfile?.companyName || '';

    const renderLogo = () => {
        if (isLoading || companyLoading) {
            return (
                <>
                    <Skeleton
                        variant="rounded"
                        width={isIcon ? 30 : 24}
                        height={24}
                        animation="wave"
                        sx={{
                            borderRadius: 1,
                            flexShrink: 0,
                        }}
                    />
                    {open && (
                        <Skeleton
                            variant="text"
                            width={120}
                            height={24}
                            animation="wave"
                            sx={{ borderRadius: 1 }}
                        />
                    )}
                </>
            );
        }

        if (logoUrl) {
            return (
                <>
                    <img
                        src={logoUrl}
                        alt={contractorCompanyName || 'Contractor Company Logo'}
                        style={{
                            width: isIcon ? 30 : '100%',
                            height: isIcon ? 30 : '100%',
                            maxWidth: '31px',
                            maxHeight: '31px',
                            objectFit: 'contain',
                            display: 'block',
                            margin: open ? '0' : '0 auto'
                        }}
                    />
                    {open && (
                        <Tooltip title={contractorCompanyName}>
                            <Typography
                                fontSize={16}
                                component="div"
                                sx={{
                                    textAlign: 'center',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    color: '#262626'
                                }}
                            >
                                {contractorCompanyName}
                            </Typography>
                        </Tooltip>
                    )}
                </>
            );
        }

        const initials = contractorCompanyName
            .split(' ')
            .map(n => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();

        return (
            <>
                <Avatar
                    variant="circular"
                    sx={{
                        width: 30,
                        height: 30,
                        bgcolor: '#D9D9D9',
                        color: '#262626',
                        fontWeight: 500,
                        fontSize: '11px'
                    }}
                >
                    {initials}
                </Avatar>
                {open && (
                    <Tooltip title={contractorCompanyName}>
                        <Typography
                            fontSize={16}
                            component="div"
                            sx={{
                                textAlign: 'center',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                color: '#262626'
                            }}
                        >
                            {contractorCompanyName}
                        </Typography>
                    </Tooltip>
                )}
            </>
        );
    };

    return (
        <Box
            sx={{ padding: 1, borderRadius: 1.5, background: open ? theme.palette.secondary[100] : '', m: open ? '10px 24px 0px' : '', cursor: 'pointer' }}
            display="flex"
            alignItems="center"
            justifyContent={open ? 'flex-start' : "center"}
            onClick={() => navigate('/contractors/companyprofile')}
            gap={1}
        >
            {renderLogo()}
        </Box>
    );
}

export default DrawerContractorCompanyHeader;
